import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface HelpIconItem {
    icon: string;
    color: string;
    title: string;
    description: string;
}

export interface HelpLegendItem {
    /** Color del punto/icono */
    color: string;
    /** Etiqueta (nombre del color o de la acción) */
    label: string;
    /** Explicación */
    description: string;
    /** Icono opcional (clase PrimeIcons). Si no se indica, se muestra un círculo del color */
    icon?: string;
}

export interface HelpSection {
    title: string;
    description?: string;
    icons?: HelpIconItem[];
    legend?: HelpLegendItem[];
    notes?: string[];
}

export interface HelpStep {
    /** Selector CSS del elemento de la vista a resaltar */
    selector: string;
    title: string;
    /** Descripción en texto (opcional si se usa legend) */
    description?: string;
    /** Icono opcional (clase PrimeIcons) */
    icon?: string;
    /** Color opcional para el icono/punto del paso */
    color?: string;
    /** Leyenda visual en formato tabla: Color = significado */
    legend?: HelpLegendItem[];
    /** Si es true, resalta TODOS los elementos que coincidan (misma fila de la tabla si aplica) */
    multiple?: boolean;
    /** Fuerza el recuadro rectangular (con margen extra de 10px) aunque el elemento sea pequeño */
    forceRect?: boolean;
}

export interface HelpContent {
    title: string;
    /** Pasos del tour guiado (resaltan elementos reales de la pantalla) */
    steps?: HelpStep[];
    /** Guía completa en texto (se muestra como respaldo o "guía completa") */
    sections?: HelpSection[];
    /** Mensaje a mostrar cuando la vista no tiene datos (no hay elementos para resaltar) */
    noDataMessage?: string;
    /** Selectores que se clickean antes de iniciar el tour (ej: expandir el detalle de la primera fila) */
    prepare?: string[];
}

@Injectable({ providedIn: 'root' })
export class HelpContentService {
    /** Solicitud de apertura/cierre del tour guiado (disparada desde el topbar) */
    private readonly tourRequestSubject = new Subject<void>();

    requestTour(): void {
        this.tourRequestSubject.next();
    }

    onTourRequested(): Observable<void> {
        return this.tourRequestSubject.asObservable();
    }

    private readonly helpByRoute: { [route: string]: HelpContent } = {
        '/sys/clinical/list': {
            title: 'Ayuda - Listado de Órdenes',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            steps: [
                {
                    selector: '[data-help="order-number"]',
                    title: 'N° de Orden y su color',
                    legend: [
                        { color: '#16a34a', label: 'Verde', description: 'La orden está totalmente paga (cliente y/o seguro, según corresponda).' },
                        { color: '#f97316', label: 'Naranja', description: 'Pago parcial: una parte está pagada y otra pendiente.' },
                        { color: '#dc2626', label: 'Rojo', description: 'La orden todavía no tiene pagos registrados.' },
                        { color: '#3b82f6', label: 'Azul', description: 'La orden está CANCELADA. No se puede operar sobre ella.' },
                        { color: '#eab308', label: 'Amarillo', description: 'La orden es de Repetición o Gentileza (estudios sin costo para el paciente).' }
                    ],
                    color: '#3b82f6'
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' },
                        { color: '#dc2626', label: 'Pendiente de Laboratorio', description: 'Estudios derivados al laboratorio, pendientes de resultado.' },
                        { color: '#64748b', label: 'Cancelado', description: 'La orden fue anulada (total o parcialmente). No se puede operar.' }
                    ]
                },
                {
                    selector: '[data-help="btn-files"]',
                    title: 'Imagen de la orden',
                    legend: [
                        { icon: 'pi pi-image', color: '#3b82f6', label: 'Ver archivos', description: 'Muestra las imágenes y archivos adjuntos de la orden.' },
                        { icon: 'pi pi-file-plus', color: '#f59e0b', label: 'Adjuntar imagen', description: 'Permite adjuntar una imagen a la orden.' }
                    ]
                },
                {
                    selector: '[data-help="btn-actions"]',
                    title: 'Acciones de la orden',
                    legend: [
                        { icon: 'pi pi-file-check', color: '#16a34a', label: 'Ver Factura', description: 'La orden ya tiene factura emitida: la abre para verla/imprimirla.' },
                        { icon: 'pi pi-print', color: '#3b82f6', label: 'Imprimir Factura', description: 'La orden aún no tiene factura y tiene monto a facturar: genera e imprime la factura.' },
                        { icon: 'pi pi-ticket', color: '#16a34a', label: 'Imprimir etiqueta', description: 'Imprime las etiquetas de los estudios de la orden.' },
                        {
                            icon: 'pi pi-times-circle',
                            color: '#dc2626',
                            label: 'Cancelar Factura/Orden',
                            description:
                                'Abre las opciones según el caso: Re-Emitir Factura, Editar Orden (mismo día), Anular con Nota de Crédito (días anteriores) o Anular Orden (combos, facturación mixta o cobertura 100%). Las opciones son las mismas para todos los roles; solo cambian las autorizaciones requeridas.'
                        }
                    ]
                },
                {
                    selector: '[data-help="btn-send"]',
                    title: 'Envío de la factura',
                    legend: [
                        { icon: 'pi pi-send', color: '#3b82f6', label: 'Enviar factura', description: 'Envía la factura al correo del cliente/doctor.' },
                        { icon: 'pi pi-history', color: '#f59e0b', label: 'Historial', description: 'Muestra el historial de correos enviados de la factura.' }
                    ]
                },
                {
                    selector: '[data-help="btn-delivered"]',
                    title: 'Entregado',
                    legend: [
                        { icon: 'pi pi-check', color: '#16a34a', label: 'Check verde', description: 'Todos los estudios fueron entregados al paciente.' },
                        { icon: 'pi pi-times', color: '#f59e0b', label: 'X naranja', description: 'Entrega parcial: solo una parte de los estudios fue entregada.' },
                        { icon: 'pi pi-times', color: '#dc2626', label: 'X roja', description: 'Ningún estudio fue entregado todavía.' }
                    ]
                }
            ],
            sections: [
                {
                    title: '¿Qué significa el color del N° de Orden?',
                    description: 'El color indica el estado de pago de la orden. Al pasar el mouse por el número se detalla si el CLIENTE y/o el SEGURO están PAGADOS o PENDIENTES.',
                    legend: [
                        { color: '#16a34a', label: 'Verde', description: 'La orden está totalmente paga (cliente y/o seguro, según corresponda).' },
                        { color: '#f97316', label: 'Naranja', description: 'Pago parcial: una parte está pagada y otra pendiente.' },
                        { color: '#dc2626', label: 'Rojo', description: 'La orden todavía no tiene pagos registrados.' },
                        { color: '#3b82f6', label: 'Azul', description: 'La orden está CANCELADA. No se puede operar sobre ella.' },
                        { color: '#eab308', label: 'Amarillo', description: 'La orden es de Repetición o Gentileza (estudios sin costo para el paciente).' }
                    ]
                },
                {
                    title: 'Estados de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' },
                        { color: '#dc2626', label: 'Pendiente de Laboratorio', description: 'Estudios derivados al laboratorio, pendientes de resultado.' },
                        { color: '#64748b', label: 'Cancelado', description: 'La orden fue anulada (total o parcialmente). No se puede operar.' }
                    ]
                },
                {
                    title: 'Iconos de la columna "Imagen"',
                    icons: [
                        { icon: 'pi pi-image', color: '#3b82f6', title: 'Ver archivos', description: 'Muestra las imágenes y archivos adjuntos de la orden.' },
                        { icon: 'pi pi-file-plus', color: '#f59e0b', title: 'Adjuntar imagen', description: 'Permite adjuntar una imagen a la orden.' }
                    ]
                },
                {
                    title: 'Iconos de la columna "Acciones"',
                    icons: [
                        { icon: 'pi pi-file-check', color: '#16a34a', title: 'Ver Factura', description: 'La orden ya tiene factura emitida: la abre para ver/imprimir.' },
                        { icon: 'pi pi-print', color: '#3b82f6', title: 'Imprimir Factura', description: 'La orden aún no tiene factura y tiene monto a facturar: genera e imprime la factura.' },
                        { icon: 'pi pi-ticket', color: '#16a34a', title: 'Imprimir etiqueta', description: 'Imprime las etiquetas de los estudios de la orden.' },
                        {
                            icon: 'pi pi-times-circle',
                            color: '#dc2626',
                            title: 'Cancelar Factura/Orden',
                            description: 'Abre las opciones: Re-Emitir Factura, Editar Orden (mismo día), Anular con Nota de Crédito (días anteriores) o Anular Orden según el caso.'
                        }
                    ]
                },
                {
                    title: 'Iconos de la columna "Envío"',
                    icons: [
                        { icon: 'pi pi-send', color: '#3b82f6', title: 'Enviar Factura por Correo', description: 'Envía la factura al correo del cliente/doctor.' },
                        { icon: 'pi pi-history', color: '#f59e0b', title: 'Historial de envíos', description: 'Muestra el historial de correos enviados de la factura.' }
                    ]
                },
                {
                    title: 'Iconos de la columna "Entregado"',
                    icons: [
                        { icon: 'pi pi-check', color: '#16a34a', title: 'Todos entregados', description: 'Todos los estudios fueron entregados al paciente.' },
                        { icon: 'pi pi-times', color: '#f59e0b', title: 'Entrega parcial', description: 'Solo una parte de los estudios fue entregada.' },
                        { icon: 'pi pi-times', color: '#dc2626', title: 'Sin entregar', description: 'Ningún estudio fue entregado todavía.' }
                    ]
                },
                {
                    title: 'Otros datos útiles',
                    notes: [
                        'Hacé clic en el N° de orden, la cédula o la fila para ver el detalle completo de la orden.',
                        'El icono de copiar (junto al N° de orden y a la cédula) copia el dato al portapapeles.',
                        'En la pestaña "Facturación Pendiente" podés seleccionar varias órdenes con el checkbox y facturarlas juntas.'
                    ]
                }
            ]
        },
        '/sys/clinical/pendiente': {
            title: 'Ayuda - Órdenes Generadas',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            prepare: ['[data-help="expand-detail"]'],
            steps: [
                {
                    selector: '[data-help="expand-detail"]',
                    title: 'Abrir el detalle de la orden',
                    legend: [
                        {
                            icon: 'pi pi-chevron-right',
                            color: '#64748b',
                            label: 'Expandir',
                            description: 'Hacé clic en este botón para abrir o cerrar el detalle de la orden, donde se listan todos sus estudios con su estado y acciones.'
                        }
                    ]
                },
                {
                    selector: '[data-help="order-date"]',
                    title: 'Fecha de la orden',
                    legend: [{ icon: 'pi pi-calendar', color: '#3b82f6', label: 'Fecha', description: 'Fecha de generación de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' },
                        { color: '#dc2626', label: 'Pendiente de Laboratorio', description: 'Estudios derivados al laboratorio, pendientes de resultado.' }
                    ]
                },
                {
                    selector: '[data-help="order-image"]',
                    title: 'Imagen de la orden',
                    legend: [
                        {
                            icon: 'pi pi-image',
                            color: '#3b82f6',
                            label: 'Ver imagen',
                            description: 'Muestra la imagen de la orden escaneada al crear la Orden de Trabajo, junto con los demás archivos adjuntos.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-status"]',
                    title: 'Estado del estudio (detalle)',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'El estudio aún no inició.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'El estudio está en curso.' },
                        { color: '#16a34a', label: 'Completado', description: 'El estudio fue finalizado.' },
                        { color: '#3b82f6', label: 'Enviado', description: 'El estudio fue enviado al paciente.' },
                        { color: '#dc2626', label: 'Pendiente en laboratorio / Pendiente de Laboratorio', description: 'El estudio está en laboratorio, pendiente de resultado.' }
                    ]
                },
                {
                    selector: '[data-help="detail-actions"]',
                    title: 'Acciones del estudio (detalle)',
                    legend: [
                        { icon: 'pi pi-play', color: '#3b82f6', label: 'Poner en proceso', description: 'Pasa el estudio de Pendiente a En proceso.' },
                        { icon: 'pi pi-stop', color: '#dc2626', label: 'Completar estudio', description: 'Finaliza el estudio (pasa a Completado).' },
                        { icon: 'pi pi-replay', color: '#64748b', label: 'Repetir estudio', description: 'Marca el estudio como repetición; pide el motivo obligatorio.' }
                    ]
                }
            ]
        },
        '/sys/clinical/pendent-laboratory': {
            title: 'Ayuda - Pendientes de Laboratorio',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            prepare: ['[data-help="expand-detail"]'],
            steps: [
                {
                    selector: '[data-help="expand-detail"]',
                    title: 'Abrir el detalle de la orden',
                    legend: [
                        {
                            icon: 'pi pi-chevron-right',
                            color: '#64748b',
                            label: 'Expandir',
                            description: 'Hacé clic en este botón para abrir o cerrar el detalle de la orden, donde se listan todos sus estudios con su estado y acciones.'
                        }
                    ]
                },
                {
                    selector: '[data-help="order-date"]',
                    title: 'Fecha de la orden',
                    legend: [{ icon: 'pi pi-calendar', color: '#3b82f6', label: 'Fecha', description: 'Fecha de generación de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' }
                    ]
                },
                {
                    selector: '[data-help="order-image"]',
                    title: 'Imagen de la orden',
                    legend: [
                        {
                            icon: 'pi pi-image',
                            color: '#3b82f6',
                            label: 'Ver imagen',
                            description: 'Muestra la imagen de la orden escaneada al crear la Orden de Trabajo, junto con los demás archivos adjuntos.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-status"]',
                    title: 'Estado del estudio (detalle)',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'El estudio aún no inició.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'El estudio está en curso.' },
                        { color: '#16a34a', label: 'Completado', description: 'El estudio fue finalizado.' },
                        { color: '#3b82f6', label: 'Enviado', description: 'El estudio fue enviado.' },
                        { color: '#dc2626', label: 'Pendiente en Laboratorio', description: 'El estudio está en laboratorio, pendiente de inicio.' },
                        { color: '#f59e0b', label: 'En proceso en Laboratorio', description: 'El estudio está en proceso dentro del laboratorio.' },
                        { color: '#16a34a', label: 'Completado en Laboratorio', description: 'El estudio fue completado en el laboratorio.' }
                    ]
                },
                {
                    selector: '[data-help="detail-actions"]',
                    title: 'Acciones del estudio (detalle)',
                    legend: [
                        { icon: 'pi pi-play', color: '#3b82f6', label: 'Asignar trabajador e iniciar', description: 'Asigna un trabajador del laboratorio y pone el estudio en proceso.' },
                        { icon: 'pi pi-stop', color: '#dc2626', label: 'Completar estudio', description: 'Finaliza el estudio (pasa a Completado).' },
                        { icon: 'pi pi-replay', color: '#64748b', label: 'Repetir estudio', description: 'Marca el estudio como repetición; pide el motivo obligatorio.' }
                    ]
                }
            ]
        },
        '/sys/clinical/list-laboratory': {
            title: 'Ayuda - Listado de Órdenes de Trabajo (Laboratorio)',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            steps: [
                {
                    selector: '[data-help="order-number"]',
                    title: 'N° de Orden y su color',
                    legend: [
                        { color: '#16a34a', label: 'Verde', description: 'La orden está totalmente paga (cliente y/o seguro, según corresponda).' },
                        { color: '#f97316', label: 'Naranja', description: 'Pago parcial: una parte está pagada y otra pendiente.' },
                        { color: '#dc2626', label: 'Rojo', description: 'La orden todavía no tiene pagos registrados.' },
                        { color: '#3b82f6', label: 'Azul', description: 'La orden está CANCELADA. No se puede operar sobre ella.' },
                        { color: '#eab308', label: 'Amarillo', description: 'La orden es de Repetición o Gentileza (estudios sin costo para el paciente).' }
                    ]
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' },
                        { color: '#64748b', label: 'Cancelado', description: 'La orden fue anulada. No se puede operar sobre ella.' }
                    ]
                },
                {
                    selector: '[data-help="btn-files"]',
                    title: 'Imagen de la orden',
                    legend: [
                        {
                            icon: 'pi pi-image',
                            color: '#3b82f6',
                            label: 'Ver imagen',
                            description: 'Muestra la imagen de la orden escaneada al crear la Orden de Trabajo, junto con los demás archivos adjuntos.'
                        },
                        { icon: 'pi pi-file-plus', color: '#f59e0b', label: 'Adjuntar imagen', description: 'Permite adjuntar una imagen a la orden.' }
                    ]
                },
                {
                    selector: '[data-help="btn-actions"]',
                    title: 'Acciones de la orden',
                    legend: [
                        { icon: 'pi pi-file-check', color: '#16a34a', label: 'Ver Factura', description: 'La orden ya tiene factura emitida: la abre para verla/imprimirla.' },
                        { icon: 'pi pi-print', color: '#3b82f6', label: 'Imprimir Factura', description: 'La orden aún no tiene factura y tiene monto a facturar: genera e imprime la factura.' },
                        { icon: 'pi pi-receipt', color: '#16a34a', label: 'Recibo emitido', description: 'La orden ya tiene recibo emitido: permite verlo/reimprimirlo.' },
                        { icon: 'pi pi-print', color: '#dc2626', label: 'Emitir Recibo', description: 'La orden está pagada pero aún no tiene recibo: permite generarlo.' },
                        { icon: 'pi pi-ticket', color: '#16a34a', label: 'Imprimir etiqueta', description: 'Imprime las etiquetas de los estudios de la orden.' },
                        { icon: 'pi pi-times-circle', color: '#dc2626', label: 'Cancelar Factura/Orden', description: 'Abre las opciones de anulación según el caso (re-emitir, editar, anular con NC, etc.).' }
                    ]
                },
                {
                    selector: '[data-help="btn-send"]',
                    title: 'Envío de la factura',
                    legend: [
                        { icon: 'pi pi-send', color: '#3b82f6', label: 'Enviar factura', description: 'Envía la factura al correo del cliente/doctor.' },
                        { icon: 'pi pi-history', color: '#f59e0b', label: 'Historial', description: 'Muestra el historial de correos enviados de la factura.' }
                    ]
                },
                {
                    selector: '[data-help="btn-delivered"]',
                    title: 'Entregado',
                    multiple: true,
                    legend: [
                        { icon: 'pi pi-check', color: '#16a34a', label: 'Check verde', description: 'Todos los estudios fueron entregados al paciente.' },
                        { icon: 'pi pi-times', color: '#f59e0b', label: 'X naranja', description: 'Entrega parcial: solo una parte de los estudios fue entregada.' },
                        { icon: 'pi pi-times', color: '#dc2626', label: 'X roja', description: 'Ningún estudio fue entregado todavía.' }
                    ]
                }
            ]
        },
        '/sys/clinical/resultado': {
            title: 'Ayuda - Resultados',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            prepare: ['[data-help="expand-detail"]'],
            steps: [
                {
                    selector: '[data-help="expand-detail"]',
                    title: 'Abrir el detalle de la orden',
                    legend: [
                        {
                            icon: 'pi pi-chevron-right',
                            color: '#64748b',
                            label: 'Expandir',
                            description: 'Hacé clic en este botón para abrir o cerrar el detalle de la orden, donde se listan todos sus estudios con sus resultados y acciones.'
                        }
                    ]
                },
                {
                    selector: '[data-help="order-number"]',
                    title: 'Nº de Orden',
                    legend: [{ icon: 'pi pi-hashtag', color: '#3b82f6', label: 'Nº de Orden', description: 'Número único de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-date"]',
                    title: 'Fecha de la orden',
                    legend: [{ icon: 'pi pi-calendar', color: '#3b82f6', label: 'Fecha', description: 'Fecha de generación de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' },
                        { color: '#dc2626', label: 'Pendiente de Laboratorio', description: 'Estudios derivados al laboratorio, pendientes de resultado.' }
                    ]
                },
                {
                    selector: '[data-help="order-results"]',
                    title: 'Resultados (cabecera)',
                    forceRect: true,
                    legend: [
                        {
                            icon: 'pi pi-file',
                            color: '#16a34a',
                            label: 'Cantidad de resultados',
                            description: 'Total de archivos/resultados cargados en la orden. VERDE = tiene resultados cargados. ROJO = todavía no tiene resultados.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-doctor"]',
                    title: 'Doctor y correo',
                    legend: [
                        { icon: 'pi pi-user', color: '#3b82f6', label: 'Dr.', description: 'Nombre del doctor al que pertenece la orden.' },
                        { icon: 'pi pi-envelope', color: '#f59e0b', label: 'Correo', description: 'Correo del doctor; es el destino al enviar los resultados.' }
                    ]
                },
                {
                    selector: '[data-help="detail-status"]',
                    title: 'Estado del estudio (detalle)',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'El estudio aún no inició.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'El estudio está en curso.' },
                        { color: '#16a34a', label: 'Completado', description: 'El estudio fue finalizado.' },
                        { color: '#3b82f6', label: 'Enviado', description: 'El estudio fue enviado al doctor/paciente.' },
                        { color: '#dc2626', label: 'Pendiente en Laboratorio', description: 'El estudio está en laboratorio, pendiente de inicio.' },
                        { color: '#f59e0b', label: 'En proceso en Laboratorio', description: 'El estudio está en proceso dentro del laboratorio.' },
                        { color: '#16a34a', label: 'Completado en Laboratorio', description: 'El estudio fue completado en el laboratorio.' }
                    ]
                },
                {
                    selector: '[data-help="detail-attach"]',
                    title: 'Adjuntar Estudio',
                    legend: [
                        { icon: 'pi pi-image', color: '#3b82f6', label: 'Ver imágenes', description: 'Muestra las imágenes/archivos cargados del estudio.' },
                        { icon: 'pi pi-file-plus', color: '#f59e0b', label: 'Adjuntar estudio', description: 'Permite cargar los resultados (archivos) del estudio.' }
                    ]
                },
                {
                    selector: '[data-help="detail-results"]',
                    title: 'Resultados del estudio',
                    forceRect: true,
                    legend: [
                        {
                            icon: 'pi pi-file',
                            color: '#16a34a',
                            label: 'Cantidad de resultados',
                            description: 'Cantidad de archivos del estudio. VERDE = tiene resultados. ROJO = sin resultados.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-send"]',
                    title: 'Envío',
                    legend: [
                        { icon: 'pi pi-send', color: '#3b82f6', label: 'Enviar estudio', description: 'Envía los resultados del estudio al correo del doctor.' },
                        { icon: 'pi pi-history', color: '#f59e0b', label: 'Historial', description: 'Muestra el historial de envíos del estudio.' }
                    ]
                }
            ]
        },
        '/sys/clinical/result-laboratory': {
            title: 'Ayuda - Resultados (Laboratorio)',
            noDataMessage: 'Seleccione un rango de fecha diferente para visualizar datos en la tabla y mostrar la ayuda.',
            prepare: ['[data-help="expand-detail"]'],
            steps: [
                {
                    selector: '[data-help="expand-detail"]',
                    title: 'Abrir el detalle de la orden',
                    legend: [
                        {
                            icon: 'pi pi-chevron-right',
                            color: '#64748b',
                            label: 'Expandir',
                            description: 'Hacé clic en este botón para abrir o cerrar el detalle de la orden, donde se listan todos sus estudios con sus resultados y acciones.'
                        }
                    ]
                },
                {
                    selector: '[data-help="order-number"]',
                    title: 'Nº de Orden',
                    legend: [{ icon: 'pi pi-hashtag', color: '#3b82f6', label: 'Nº de Orden', description: 'Número único de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-date"]',
                    title: 'Fecha de la orden',
                    legend: [{ icon: 'pi pi-calendar', color: '#3b82f6', label: 'Fecha', description: 'Fecha de generación de la orden de trabajo.' }]
                },
                {
                    selector: '[data-help="order-status"]',
                    title: 'Estado de la orden',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'La orden fue creada y aún no inicia su flujo de trabajo.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'La orden está en curso (estudios realizándose).' },
                        { color: '#f59e0b', label: 'Incompleto', description: 'Falta completar estudios o entregas de la orden.' },
                        { color: '#16a34a', label: 'Completado', description: 'Todos los estudios de la orden finalizaron.' }
                    ]
                },
                {
                    selector: '[data-help="order-results"]',
                    title: 'Resultados (cabecera)',
                    forceRect: true,
                    legend: [
                        {
                            icon: 'pi pi-file',
                            color: '#16a34a',
                            label: 'Cantidad de resultados',
                            description: 'Total de archivos/resultados cargados en la orden. VERDE = tiene resultados cargados. ROJO = todavía no tiene resultados.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-doctor"]',
                    title: 'Doctor y correo',
                    legend: [
                        { icon: 'pi pi-user', color: '#3b82f6', label: 'Dr.', description: 'Nombre del doctor al que pertenece la orden.' },
                        { icon: 'pi pi-envelope', color: '#f59e0b', label: 'Correo', description: 'Correo del doctor; es el destino al enviar los resultados.' }
                    ]
                },
                {
                    selector: '[data-help="detail-status"]',
                    title: 'Estado del estudio (detalle)',
                    legend: [
                        { color: '#dc2626', label: 'Pendiente', description: 'El estudio aún no inició.' },
                        { color: '#f59e0b', label: 'En proceso', description: 'El estudio está en curso.' },
                        { color: '#16a34a', label: 'Completado', description: 'El estudio fue finalizado.' },
                        { color: '#3b82f6', label: 'Enviado', description: 'El estudio fue enviado al doctor/paciente.' },
                        { color: '#dc2626', label: 'Pendiente en Laboratorio', description: 'El estudio está en laboratorio, pendiente de inicio.' },
                        { color: '#f59e0b', label: 'En proceso en Laboratorio', description: 'El estudio está en proceso dentro del laboratorio.' },
                        { color: '#16a34a', label: 'Completado en Laboratorio', description: 'El estudio fue completado en el laboratorio.' }
                    ]
                },
                {
                    selector: '[data-help="detail-attach"]',
                    title: 'Adjuntar Estudio',
                    legend: [
                        { icon: 'pi pi-image', color: '#3b82f6', label: 'Ver imágenes', description: 'Muestra las imágenes/archivos cargados del estudio.' },
                        { icon: 'pi pi-file-plus', color: '#f59e0b', label: 'Adjuntar estudio', description: 'Permite cargar los resultados (archivos) del estudio.' }
                    ]
                },
                {
                    selector: '[data-help="detail-results"]',
                    title: 'Resultados del estudio',
                    forceRect: true,
                    legend: [
                        {
                            icon: 'pi pi-file',
                            color: '#16a34a',
                            label: 'Cantidad de resultados',
                            description: 'Cantidad de archivos del estudio. VERDE = tiene resultados. ROJO = sin resultados.'
                        }
                    ]
                },
                {
                    selector: '[data-help="detail-send"]',
                    title: 'Envío',
                    legend: [
                        { icon: 'pi pi-send', color: '#3b82f6', label: 'Enviar estudio', description: 'Envía los resultados del estudio al correo del doctor.' },
                        { icon: 'pi pi-history', color: '#f59e0b', label: 'Historial', description: 'Muestra el historial de envíos del estudio.' }
                    ]
                }
            ]
        }
    };

    getHelpForUrl(url: string): HelpContent | null {
        const clean = this.normalizeUrl(url);
        return this.helpByRoute[clean] ?? null;
    }

    private normalizeUrl(url: string): string {
        if (!url) return '';
        return url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    }
}
